import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Table,
  Database,
  Presentation,
  Mail,
  Calendar,
  BarChart3,
  Users,
  FileSpreadsheet,
  BookOpen,
  Award,
  Clock,
} from "lucide-react";
import Navbar from "../../../sub-comp/Navbar";
import office from "../../../../assets/office.jpeg";
import FullScreenImage from "../../../Home/ShowcaseSection";
import Footer from "../../../sub-comp/MainFooter";

const ComputerApplications = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [visibleCards, setVisibleCards] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 200);

    // Animate cards in sequence
    const cardTimer = setTimeout(() => {
      Array.from({ length: 8 }, (_, i) => i).forEach((index) => {
        setTimeout(() => {
          setVisibleCards((prev) => [...prev, index]);
        }, index * 200);
      });
    }, 800);

    return () => {
      clearTimeout(timer);
      clearTimeout(cardTimer);
    };
  }, []);

  const msOfficeApps = [
    {
      icon: <FileText className="w-12 h-12 text-blue-600" />,
      name: "Microsoft Word",
      description: "Document creation, formatting, and professional writing",
      features: [
        "Document formatting",
        "Templates",
        "Mail merge",
        "Collaboration",
      ],
      color: "blue",
    },
    {
      icon: <FileSpreadsheet className="w-12 h-12 text-green-600" />,
      name: "Microsoft Excel",
      description: "Spreadsheet analysis, data visualization, and calculations",
      features: [
        "Formulas & Functions",
        "Pivot Tables",
        "Charts",
        "Data Analysis",
      ],
      color: "green",
    },
    {
      icon: <Presentation className="w-12 h-12 text-orange-600" />,
      name: "MicrosowerPoint",
      description: "Dynamic presentations and visual storytelling",
      features: ["Slide design", "Animations", "Templates", "Multimedia"],
      color: "orange",
    },
    {
      icon: <Database className="w-12 h-12 text-red-600" />,
      name: "Microsoft Access",
      description: "Database management and data organization",
      features: ["Database design", "Forms", "Reports", "Queries"],
      color: "red",
    },
    {
      icon: <Mail className="w-12 h-12 text-blue-500" />,
      name: "Microsoft Outlook",
      description: "Email management and communication",
      features: ["Email organization", "Calendar", "Contacts", "Tasks"],
      color: "blue",
    },
    {
      icon: <Users className="w-12 h-12 text-purple-600" />,
      name: "Microsoft Teams",
      description: "Collaboration and remote work solutions",
      features: ["Video meetings", "File sharing", "Chat", "Integrations"],
      color: "purple",
    },
    {
      icon: <BookOpen className="w-12 h-12 text-indigo-600" />,
      name: "Microsoft OneNote",
      description: "Digital note-taking and organization",
      features: [
        "Note organization",
        "Sync across devices",
        "Search",
        "Sharing",
      ],
      color: "indigo",
    },
    {
      icon: <BookOpen className="w-12 h-12 text-green-600" />,
      name: "Microsoft Publisher",
      description: "Design and publish professional documents",
      features: ["Brochures", "Flyers", "Newsletters", "Templates"],
      color: "green",
    },
  ];

  const skillLevels = [
    {
      title: "Beginner Level",
      duration: "2 months",
      apps: ["Word Basics", "Excel Fundamentals", "PowerPoint Essentials"],
      icon: <Award className="w-6 h-6 text-custom-pink" />,
    },
    {
      title: "Intermediate Level",
      duration: "3 months",
      apps: ["Advanced Excel", "Access Database", "Outlook Management"],
      icon: <Award className="w-6 h-6 text-custom-pink" />,
    },
    {
      title: "Advanced Level",
      duration: "3 months",
      apps: ["Excel Macros", "Advanced Access", "Teams & Collaboration"],
      icon: <Award className="w-6 h-6 text-custom-pink" />,
    },
  ];

  return (
    <div>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Hero Section with Split Design */}
        <div className="relative h-screen flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={office}
              alt="Microsoft Office Applications"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-custom-pink/90 via-blue-900/80 to-purple-900/90"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div
              className={`text-white transform transition-all duration-1000 ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-full opacity-0"
              }`}
            >
              <div className="mb-6">
                <Link
                  to="/course/information-technology"
                  className="flex items-center text-white/80 hover:text-white font-medium transition-all duration-300 hover:translate-x-1 group mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:-translate-x-1" />
                  Back to IT Programs
                </Link>
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                Microsoft Office
                <span className="block text-yellow-400">Applications</span>
              </h1>
              <p className="text-xl mb-8 text-white/90">
                Master all Microsoft Office applications and boost your
                productivity with comprehensive training
              </p>
            </div>

            {/* Right Content - Floating Cards */}
            <div
              className={`transform transition-all duration-1000 delay-500 ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "translate-x-full opacity-0"
              }`}
            >
              <div className="grid grid-cols-2 gap-4">
                {msOfficeApps.slice(0, 4).map((app, index) => (
                  <div
                    key={index}
                    className={`bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 ${
                      index % 2 === 0 ? "animate-bounce" : "animate-pulse"
                    }`}
                    style={{
                      animationDelay: `${index * 0.5}s`,
                      animationDuration: "3s",
                    }}
                  >
                    {app.icon}
                    <h3 className="text-white font-semibold mt-2">
                      {app.name}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Microsoft Office Applications Grid */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div
              className={`text-center mb-16 transform transition-all duration-800 ${
                isVisible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-8 opacity-0"
              }`}
            >
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                Complete Microsoft Office Suite
              </h2>
              <p className="text-xl text-gray-600">
                Learn industry-standard applications used by millions worldwide
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {msOfficeApps.map((app, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl p-6 transition-all duration-500 hover:-translate-y-4 hover:rotate-1 group cursor-pointer border-l-4 border-${app.color}-500 transform ${
                    visibleCards.includes(index)
                      ? "translate-y-0 opacity-100 scale-100"
                      : "translate-y-12 opacity-0 scale-90"
                  }`}
                >
                  <div className="flex items-center mb-4">
                    <div className="transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                      {app.icon}
                    </div>
                    <div className="ml-4">
                      <h3
                        className={`font-bold text-gray-800 group-hover:text-${app.color}-600 transition-colors duration-300`}
                      >
                        {app.name}
                      </h3>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4 text-sm">
                    {app.description}
                  </p>
                  <div className="space-y-2">
                    {app.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className={`flex items-center text-sm text-gray-500 transform transition-all duration-300 delay-${featureIndex * 100} group-hover:translate-x-2 group-hover:text-${app.color}-600`}
                      >
                        <div
                          className={`w-2 h-2 bg-${app.color}-500 rounded-full mr-2`}
                        ></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Skill Levels Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-custom-pink/5 to-blue-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                Learning Path
              </h2>
              <p className="text-xl text-gray-600">
                Progress from beginner to advanced with our structured
                curriculum
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {skillLevels.map((level, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group transform ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-8 opacity-0"
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <div className="mb-6 flex justify-center">
                    <div className="bg-custom-pink/10 p-4 rounded-full group-hover:bg-custom-pink/20 transition-colors duration-300">
                      {level.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-custom-pink transition-colors duration-300">
                    {level.title}
                  </h3>
                  <div className="flex items-center justify-center mb-4 text-gray-600">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{level.duration}</span>
                  </div>
                  <div className="space-y-2">
                    {level.apps.map((app, appIndex) => (
                      <div
                        key={appIndex}
                        className="text-gray-600 text-sm bg-gray-50 py-2 px-3 rounded-lg hover:bg-custom-pink/5 transition-colors duration-300"
                      >
                        {app}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 px-4 bg-gradient-to-r from-custom-pink to-purple-700">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl font-bold mb-6">
              Ready to Master Microsoft Office?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of students who have enhanced their productivity
              and career prospects
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-yellow-500 text-black px-8 py-4 rounded-full font-semibold text-lg hover:bg-yellow-400 transition-all duration-300 transform hover:scale-105 hover:shadow-lg">
                Start Learning Today
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-custom-pink transition-all duration-300 transform hover:scale-105">
                Download Brochure
              </button>
            </div>
          </div>
        </section>
      </div>
      <FullScreenImage />
      <Footer />
    </div>
  );
};

export default ComputerApplications;
